import React, { Suspense } from 'react';
import { HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import { Provider as ReakitProvider } from 'reakit';

import { queryClientFactory } from '@cx-modules/client';
import { I18nProvider } from '@cx-modules/i18n-provider';
import { type Betslip } from '@cx-modules/types';

import type { MostPopularBetsIndexProps } from '../contract';

import { ErrorBoundary } from './components/ErrorBoundary';
import { MostPopularBetsPropsProvider } from './context/MostPopularBetsContext';
import { MostPopularBets } from './MostPopularBets';

declare global {
  interface Window {
    bettingActions: {
      addOrRemoveFromBetslip: (
        betArray: any[],
        matchBetIds?: any[],
        bookie?: string,
        betOdds?: string,
        betName?: string,
        betLocation?: string,
        subeventName?: string,
        marketName?: string,
        eventName?: string,
      ) => void;
    };
    ocBetslip?: Betslip;
  }
}

// `dataLayer` is deliberately absent from the block above and narrowed locally instead. Since NGF-6661
// this module is compiled from source inside each consuming app's TypeScript program, where the global
// is already declared — `@cx-web/utils` types it as a non-optional `DataLayerEvent[]` — and a second
// declaration that disagrees on the type or the `?` modifier is a hard error (TS2717 / TS2687).
// `bettingActions`/`ocBetslip` stay, but NOT because nothing else declares them — four places do, and this
// block has to keep agreeing with them or every consuming app hits the same TS2717/TS2687: root
// `global.d.ts:73,111`, `apps/atlas/global.d.ts:61,92`, `apps/product-plus/app/global.d.ts:42,63` and
// `packages/features/betslip/src/standalone-build.d.ts:33-34`. It compiles today only because the
// signatures are spelled identically; adding a field or a `?` on either side breaks the other, so treat
// this block as coupled to those. (`modules/odds-grid/src/index.tsx` carries the same block, same rule.)
// The narrower alternative — dropping this block and casting at the two call sites the way `dataLayer`
// does above — is the eventual fix, but the betslip signature is large enough that it is its own change.
type WindowWithDataLayer = { dataLayer?: unknown[] };

function getDefaultDataLayer(): unknown[] | undefined {
  if (globalThis.window === undefined) {
    return undefined;
  }
  return (globalThis.window as unknown as WindowWithDataLayer).dataLayer;
}

const createQueryClient = () =>
  queryClientFactory({
    defaultOptions: {
      queries: {
        gcTime: Infinity,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retry: 3,
        retryDelay: failureCount => failureCount * 100,
        staleTime: Infinity,
      },
    },
  });

export default function MostPopularBetsWithQuery({
  dehydratedState,
  loadingElement,
  ...props
}: MostPopularBetsIndexProps) {
  const [queryClient] = React.useState(createQueryClient);

  const { analyticsDataLayer = getDefaultDataLayer() } = props;

  return (
    <ReakitProvider>
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={dehydratedState}>
          <Suspense fallback={loadingElement ?? null}>
            <MostPopularBetsPropsProvider {...props} analyticsDataLayer={analyticsDataLayer}>
              <ErrorBoundary>
                {props.i18n ? (
                  <I18nProvider i18nInstance={props.i18n}>
                    <MostPopularBets />
                  </I18nProvider>
                ) : (
                  <MostPopularBets />
                )}
              </ErrorBoundary>
            </MostPopularBetsPropsProvider>
          </Suspense>
        </HydrationBoundary>
      </QueryClientProvider>
    </ReakitProvider>
  );
}

// The props type is part of the public API — every consumer wraps this component and types its
// own props with it. Re-exported explicitly: the dist `.d.ts` dts-bundle-generator used to emit
// hoisted it automatically, and since NGF-6661 this file IS the package's type surface.
export type { MostPopularBetsIndexProps } from '../contract';
export type { Sport } from '../interfaces';

export { loadDataForServer } from './loadData';
